import React, { useCallback, useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getPreferences, listNotifications, markAllRead, markRead, setPreferences, SEVERITY_COLOR, timeAgo,
  type Group, type Notification, type Preferences,
} from '../../api/notificationsApi';

/** Notification centre (SRS #138): full history by group, plus delivery preferences (SRS #101). */
const PAGE = 50;

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [groups, setGroups] = useState<Record<string, string>>({});
  const [group, setGroup] = useState<Group | ''>('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [unread, setUnread] = useState(0);
  const [offset, setOffset] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback((off = 0) => {
    setLoading(true); setError(null);
    listNotifications({ group, unread: unreadOnly, limit: PAGE, offset: off })
      .then((r) => { setItems((xs) => (off ? [...xs, ...r.notifications] : r.notifications)); setGroups(r.groups); setUnread(r.unread); setMore(r.notifications.length === PAGE); setOffset(off); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [group, unreadOnly]);
  useEffect(() => { load(0); }, [load]);
  useEffect(() => { getPreferences().then(setPrefs).catch(() => {}); }, []);

  const open = async (n: Notification) => {
    if (!n.read) { try { await markRead([n.id]); setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x))); setUnread((u) => Math.max(0, u - 1)); } catch { /* best-effort */ } }
    if (n.link) navigate(n.link);
  };
  const savePrefs = async (p: { email_enabled?: boolean; muted_groups?: Group[] }) => {
    setSaving(true);
    try { setPrefs(await setPreferences(p)); } catch (e) { setError(e instanceof Error ? e.message : 'Could not save preferences'); } finally { setSaving(false); }
  };

  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const text = 'var(--color-text, var(--ds-text))';
  const border = 'var(--ds-border, var(--ds-border))';
  const chip = (active: boolean): React.CSSProperties => ({ padding: '5px 10px', borderRadius: 999, border: `1px solid ${active ? 'var(--ds-brand-blue)' : 'var(--ds-border-strong)'}`, background: active ? 'var(--ds-brand-blue)' : 'var(--ds-surface, #fff)', color: active ? 'var(--ds-text-on-dark)' : text, cursor: 'pointer', fontSize: 12.5 });

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Bell size={22} aria-hidden />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: text }}>Notifications</h1>
        {unread > 0 && <span style={{ fontSize: 12.5, color: muted }}>{unread} unread</span>}
        {unread > 0 && <button type="button" onClick={async () => { await markAllRead().catch(() => {}); load(0); }} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: 'var(--ds-link)', cursor: 'pointer', fontSize: 13 }}>Mark all read</button>}
      </div>
      <p style={{ margin: '0 0 18px', color: muted, fontSize: 14 }}>Renewals and payments, plan changes, support replies, team activity, regulation alert matches and announcements — all in one place.</p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 14 }}>
        <button type="button" style={chip(group === '')} onClick={() => setGroup('')}>All</button>
        {Object.entries(groups).map(([k, label]) => <button key={k} type="button" style={chip(group === k)} onClick={() => setGroup(k as Group)}>{label}</button>)}
        <label style={{ marginLeft: 'auto', fontSize: 12.5, color: muted }}><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} /> unread only</label>
      </div>

      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 14, marginBottom: 12 }}>{error}</div>}
      {loading && items.length === 0 && <p style={{ color: muted }}>Loading…</p>}
      {!loading && items.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: muted }}>Nothing here yet.</div>}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((n) => {
          const col = SEVERITY_COLOR[n.severity];
          return (
            <li key={n.id}>
              <button type="button" onClick={() => open(n)}
                      style={{ width: '100%', textAlign: 'left', display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'start', padding: '12px 14px', borderRadius: 10, border: `1px solid ${border}`, background: n.read ? 'var(--ds-surface, #fff)' : 'rgba(68,76,231,0.05)', cursor: n.link ? 'pointer' : 'default' }}>
                <span style={{ marginTop: 2, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: col.bg, color: col.fg, border: `1px solid ${col.border}`, whiteSpace: 'nowrap' }}>{groups[n.group] || n.group}</span>
                <span>
                  <span style={{ display: 'block', fontSize: 14.5, fontWeight: n.read ? 500 : 700, color: text }}>{n.title}</span>
                  {n.body && <span style={{ display: 'block', fontSize: 13, color: muted, marginTop: 3, whiteSpace: 'pre-wrap' }}>{n.body}</span>}
                  {n.link && <span style={{ display: 'inline-block', marginTop: 6, fontSize: 12.5, color: 'var(--ds-link)' }}>Open →</span>}
                </span>
                <span style={{ fontSize: 12, color: 'var(--ds-text-faint)', whiteSpace: 'nowrap' }} title={n.created_at || ''}>{timeAgo(n.created_at)}{n.email_sent ? ' · emailed' : ''}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {more && <button type="button" disabled={loading} onClick={() => load(offset + PAGE)} style={{ marginTop: 12, padding: '7px 14px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Load more</button>}

      {prefs && (
        <section style={{ marginTop: 32, border: `1px solid ${border}`, borderRadius: 12, padding: 16, background: 'var(--ds-surface, #fff)' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, color: text }}>Email preferences</h2>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: muted }}>Everything always appears here. Choose which ones are also emailed. Billing and security emails cannot be switched off.</p>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, color: text, marginBottom: 10 }}>
            <input type="checkbox" checked={prefs.email_enabled} disabled={saving} onChange={(e) => savePrefs({ email_enabled: e.target.checked })} /> Send me email notifications
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, opacity: prefs.email_enabled ? 1 : 0.5 }}>
            {Object.entries(prefs.groups).map(([k, label]) => {
              const locked = prefs.always_email.includes(k as Group);
              const on = locked || !prefs.muted_groups.includes(k as Group);
              return (
                <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: text }}>
                  <input type="checkbox" checked={on} disabled={locked || saving || !prefs.email_enabled}
                         onChange={(e) => savePrefs({ muted_groups: e.target.checked ? prefs.muted_groups.filter((g) => g !== k) : [...prefs.muted_groups, k as Group] })} />
                  {label}{locked && <span style={{ fontSize: 11, color: muted }}>(always)</span>}
                </label>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationsPage;
